import { Component } from '@theme/component';

/**
 * @typedef {Object} TextClampRefs
 * @property {HTMLElement} textClampContent - Content element to clamp
 * @property {HTMLButtonElement} textClampButton - Toggle button
 * @property {HTMLElement} [textClampDots] - Custom ellipsis indicator
 */

/**
 * Custom element that collapses text to N lines and animates expand/collapse.
 *
 * @extends {Component<TextClampRefs>}
 */
class TextClampComponent extends Component {
  requiredRefs = ['textClampContent'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {number} */
  #collapsedHeight = 0;

  /** @type {Animation | undefined} */
  #animation;

  /** @type {number} */
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();
    window.requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #init = () => {
    const { textClampContent } = this.refs;
    const lines = Number(this.dataset.maxLines) || 3;

    textClampContent.style.setProperty('--text-clamp-lines', lines);
    this.#collapsedHeight = textClampContent.clientHeight;
    // The overflow is on the child (.text-block), so check its scroll vs client height.
    const child = textClampContent.firstElementChild;
    const isTruncated = child ? child.scrollHeight - child.clientHeight > 2 : false;
    this.dataset.truncated = isTruncated ? 'true' : 'false';
  };

  /** @returns {{startHeight: number, endHeight: number}} */
  #expand = () => {
    const { textClampContent } = this.refs;
    const startHeight = this.#collapsedHeight;
    // Remove clamp first — while the child owns overflow:hidden, the wrapper's
    // scrollHeight equals clientHeight so we can't measure full height before this.
    textClampContent.classList.remove('is-clamped');
    const endHeight = textClampContent.scrollHeight;
    return { startHeight, endHeight };
  };

  /** @returns {{startHeight: number, endHeight: number}} */
  #collapse = () => {
    const { textClampContent } = this.refs;
    const startHeight = textClampContent.offsetHeight;
    return { startHeight, endHeight: this.#collapsedHeight };
  };

  /**
   * @param {number} startHeight
   * @param {number} endHeight
   */
  #animateHeight = (startHeight, endHeight) => {
    const { textClampContent } = this.refs;

    textClampContent.style.overflow = 'hidden';
    this.#animation?.cancel();

    this.#animation = textClampContent.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish();
  };

  #onAnimationFinish = () => {
    const { textClampContent } = this.refs;

    if (this.#expanded) {
      // Finished collapsing — re-apply clamp.
      textClampContent.classList.add('is-clamped');
    }

    textClampContent.style.removeProperty('height');
    textClampContent.style.overflow = '';
    this.#expanded = !this.#expanded;
  };

  /** @param {Event} event */
  toggle = (event) => {
    event.preventDefault();

    const { textClampButton } = this.refs;
    const { startHeight, endHeight } = !this.#expanded ? this.#expand() : this.#collapse();

    this.dataset.expanded = this.#expanded ? 'false' : 'true';
    textClampButton.setAttribute('aria-expanded', this.dataset.expanded);

    this.#animateHeight(startHeight, endHeight);
  };
}

if (!customElements.get('text-clamp-component')) {
  customElements.define('text-clamp-component', TextClampComponent);
}
